import React, { useState } from 'react';
import SHA256 from 'crypto-js/sha256';

// Genera un número aleatorio en [min, max]
const randomInRange = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Genera un número aleatorio con decimales entre 0 y 1 para el gas
const randomGas = () => (Math.random().toFixed(3));

// Crea una transacción aleatoria de ejemplo
const createRandomTx = () => {
  const names = ["Alice", "Bob", "Charlie", "David", "Eva", "Frank", "Gina",
                 "Henry", "Irene", "Jack", "Karen", "Leo", "Maria", "Nina",
                 "Oscar", "Paula", "Quentin", "Rose", "Steve", "Tina"];

  const fromIndex = randomInRange(0, names.length - 1);
  let toIndex = randomInRange(0, names.length - 1);
  // Asegurar que 'to' sea diferente de 'from'
  while (toIndex === fromIndex) {
    toIndex = randomInRange(0, names.length - 1);
  }

  return {
    from: names[fromIndex],
    to: names[toIndex],
    amount: randomInRange(1, 100), // Monto entre 1 y 100
    gas: randomGas() // Gas aleatorio entre 0 y 1
  };
};

// Crea un array de 20 transacciones aleatorias iniciales
const initialTxs = Array.from({ length: 20 }, () => createRandomTx());

// Función para calcular la raíz de Merkle de un conjunto de transacciones
const computeMerkleRoot = (transactions) => {
  if (transactions.length === 0) return "";
  // Cada transacción se transforma en un hash
  let level = transactions.map(tx =>
    SHA256(`${tx.from}${tx.to}${tx.amount}${tx.gas}`).toString()
  );

  // Se construye el árbol de Merkle
  while (level.length > 1) {
    const nextLevel = [];
    for (let i = 0; i < level.length; i += 2) {
      // Si hay un número impar, duplicamos el último hash
      const left = level[i];
      const right = (i + 1 < level.length) ? level[i + 1] : left;
      nextLevel.push(SHA256(left + right).toString());
    }
    level = nextLevel;
  }
  return level[0];
};

const MerkleMempoolSimulation = () => {
  // Mempool inicial con 20 transacciones
  const [mempool, setMempool] = useState(initialTxs);

  // Cadena de bloques (comienza con bloque génesis)
  const [chain, setChain] = useState([
    {
      index: 0,
      prevHash: "0",
      hash: "",
      nonce: 0,
      transactions: [],
      difficulty: 2,
      merkleRoot: ""
    }
  ]);

  // Capacidad de transacciones por bloque
  const blockCapacity = 5;

  // Campos para crear una nueva transacción manualmente
  const [txFrom, setTxFrom] = useState("");
  const [txTo, setTxTo] = useState("");
  const [txAmount, setTxAmount] = useState("");

  // Calcula el hash de un bloque
  const computeHash = (block) => {
    const { index, prevHash, nonce, transactions, merkleRoot } = block;
    // Unimos la info de cada transacción en un string
    const txData = transactions
      .map((tx) => `${tx.from}${tx.to}${tx.amount}${tx.gas}`)
      .join("");
    return SHA256(`${index}${prevHash}${nonce}${merkleRoot}${txData}`).toString();
  };

  // Verifica si un bloque es válido (hash y prevHash)
  const isBlockValid = (block, i, entireChain) => {
    if (i === 0) {
      // Bloque génesis
      return block.prevHash === "0" && block.hash === computeHash(block);
    } else {
      const prevBlock = entireChain[i - 1];
      if (block.prevHash !== prevBlock.hash) return false;
      if (block.hash !== computeHash(block)) return false;
      return true;
    }
  };

  // Función para minar un nuevo bloque
  const mineBlock = () => {
    if (mempool.length === 0) return; // No hay transacciones que minar

    const updatedChain = [...chain];
    const lastBlock = updatedChain[updatedChain.length - 1];

    // Ordenar transacciones por gas descendente
    const sortedMempool = [...mempool].sort((a, b) => parseFloat(b.gas) - parseFloat(a.gas));

    // Seleccionar las primeras 'blockCapacity' transacciones
    const selectedTxs = sortedMempool.slice(0, blockCapacity);

    // Calcular la raíz de Merkle para las transacciones seleccionadas
    const merkleRoot = computeMerkleRoot(selectedTxs);

    // Crear el nuevo bloque
    const newBlock = {
      index: updatedChain.length,
      prevHash: lastBlock.hash || "0",
      hash: "",
      nonce: 0,
      transactions: selectedTxs,
      difficulty: 2,
      merkleRoot
    };

    // Minado: buscamos un hash que empiece con "00" (dificultad=2)
    const targetPrefix = "0".repeat(newBlock.difficulty);
    let newHash = computeHash(newBlock);

    while (!newHash.startsWith(targetPrefix)) {
      newBlock.nonce++;
      newHash = computeHash(newBlock);
    }
    newBlock.hash = newHash;

    // Agregar el nuevo bloque a la cadena
    updatedChain.push(newBlock);
    setChain(updatedChain);

    // Remover las transacciones seleccionadas de la mempool
    const remainingTxs = sortedMempool.slice(blockCapacity);
    setMempool(remainingTxs);
  };

  // Función para agregar una transacción manualmente
  // Se asigna gas aleatorio para imitar la "tarifa"
  const addTransaction = () => {
    if (!txFrom || !txTo || !txAmount) return;
    const newTx = {
      from: txFrom,
      to: txTo,
      amount: parseFloat(txAmount),
      gas: randomGas() // Gas aleatorio
    };
    setMempool([...mempool, newTx]);
    setTxFrom("");
    setTxTo("");
    setTxAmount("");
  };

  // Estilos
  const containerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    justifyContent: "center",
    marginTop: "20px"
  };

  const blockStyle = (invalid) => ({
    width: "320px",
    backgroundColor: invalid ? "#ffd2d2" : "#e3f2fd",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "16px"
  });

  const labelStyle = {
    display: "block",
    fontWeight: "bold",
    marginBottom: "4px"
  };

  const inputStyle = {
    width: "100%",
    marginBottom: "8px",
    padding: "6px",
    border: "1px solid #ccc",
    borderRadius: "4px"
  };

  const buttonStyle = {
    padding: "6px 12px",
    cursor: "pointer",
    backgroundColor: "#008CBA",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    marginRight: "10px"
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h1 style={{ textAlign: "center" }}>Simulación de Mempool con Árbol de Merkle</h1>

      {/* Form para agregar transacciones manualmente */}
      <div style={{ maxWidth: "400px", margin: "0 auto" }}>
        <label style={labelStyle}>De (Remitente):</label>
        <input
          type="text"
          value={txFrom}
          onChange={(e) => setTxFrom(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Para (Destinatario):</label>
        <input
          type="text"
          value={txTo}
          onChange={(e) => setTxTo(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Monto:</label>
        <input
          type="number"
          value={txAmount}
          onChange={(e) => setTxAmount(e.target.value)}
          style={inputStyle}
        />

        <button onClick={addTransaction} style={buttonStyle}>
          Agregar Tx
        </button>
        <button onClick={mineBlock} style={buttonStyle}>
          Minar Bloque
        </button>
      </div>

      {/* Mempool */}
      <div style={{ maxWidth: "500px", margin: "20px auto" }}>
        <h3>Mempool ({mempool.length} transacciones)</h3>
        <p>Capacidad del bloque: {blockCapacity} transacciones</p>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {mempool.map((tx, idx) => (
            <li
              key={idx}
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                marginBottom: "8px",
                padding: "6px"
              }}
            >
              <strong>De:</strong> {tx.from} | <strong>Para:</strong> {tx.to} <br />
              <strong>Monto:</strong> {tx.amount} | <strong>Gas:</strong> {tx.gas}
            </li>
          ))}
        </ul>
      </div>

      {/* Cadena de bloques */}
      <h2 style={{ textAlign: "center" }}>Cadena de Bloques</h2>
      <div style={containerStyle}>
        {chain.map((block, i) => {
          const invalid = !isBlockValid(block, i, chain);
          return (
            <div key={i} style={blockStyle(invalid)}>
              <label style={labelStyle}>Bloque #{block.index}</label>
              <label style={labelStyle}>Nonce:</label>
              <input
                type="number"
                value={block.nonce}
                readOnly
                style={inputStyle}
              />

              <label style={labelStyle}>PrevHash:</label>
              <textarea
                rows={2}
                value={block.prevHash}
                readOnly
                style={{ ...inputStyle, fontSize: "12px" }}
              />

              <label style={labelStyle}>Merkle Root:</label>
              <textarea
                rows={2}
                value={block.merkleRoot}
                readOnly
                style={{ ...inputStyle, fontSize: "12px" }}
              />

              <label style={labelStyle}>Hash:</label>
              <textarea
                rows={2}
                value={block.hash}
                readOnly
                style={{ ...inputStyle, fontSize: "12px" }}
              />

              <label style={labelStyle}>Transacciones ({block.transactions.length}):</label>
              <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                {block.transactions.map((tx, txIdx) => (
                  <li key={txIdx} style={{ borderBottom: "1px solid #ccc", marginBottom: "4px" }}>
                    <strong>De:</strong> {tx.from}, <strong>Para:</strong> {tx.to}, 
                    <strong> Monto:</strong> {tx.amount}, <strong>Gas:</strong> {tx.gas}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MerkleMempoolSimulation;
